import { Redux } from '../..'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { GygEonTargets, GygEonTargetsPayload, GygEonTargetsQueryOptions } from '@/src/types/apps/gygEonTargets'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(isoWeek)

/** Only allow rollover when viewing current week or next week — never for week+2 and beyond */
function isRequestedWeekCurrentOrNext(startDate: string): boolean {
  const requested = dayjs(startDate).startOf('isoWeek')
  const currentWeekStart = dayjs().startOf('isoWeek')
  const nextWeekStart = currentWeekStart.add(7, 'day')

  return requested.isSame(currentWeekStart, 'day') || requested.isSame(nextWeekStart, 'day')
}

/** True when requested week is week+2 or later (future). Those weeks must never show rollover data. */
function isRequestedWeekFutureBeyondNext(startDate: string): boolean {
  const requested = dayjs(startDate).startOf('isoWeek')
  const nextWeekStart = dayjs().startOf('isoWeek').add(7, 'day')

  return requested.isAfter(nextWeekStart, 'day')
}

interface EonTargetsInitialState {
  targets: GygEonTargets | null
  loading: boolean
  success: string | null
  error: string | null
}

const initialState: EonTargetsInitialState = {
  targets: null,
  loading: false,
  success: null,
  error: null
}

interface EonTargetsResponse {
  data: GygEonTargets
}

/** Normalise API response to entity; treat as "has data" only if entity has weekEnding or id */
function toTargetsEntity(raw: unknown): GygEonTargets | null {
  if (raw == null) return null
  const obj = typeof raw === 'object' ? (raw as Record<string, unknown>) : null
  if (!obj) return null
  const entity = (obj.data != null && typeof obj.data === 'object' ? obj.data : obj) as Record<string, unknown>
  if (entity.weekEnding != null || entity.id != null) return entity as unknown as GygEonTargets

  return null
}

export const getEonTargetsInTwoDates = createAsyncThunk<EonTargetsResponse | null, GygEonTargetsQueryOptions, Redux>(
  'gygEonTargets/getEonTargetsInTwoDates',
  async (gygEonTargetsQueryOptions, { extra }) => {
    const { restaurantId, startDate, endDate } = gygEonTargetsQueryOptions

    let responseData: unknown
    try {
      const res = await extra.httpClient.get(`restaurants/${restaurantId}/eon-targets/latest`, {
        params: { startDate, endDate }
      })
      responseData = res?.data
    } catch (err) {
      responseData = null
    }

    const entity = toTargetsEntity(responseData)
    const hasData = entity != null

    // Never show rollover for week+2 and beyond — only current week or next week may display rolled-over data. Past weeks keep their own data.
    if (hasData && isRequestedWeekFutureBeyondNext(startDate)) {
      return null
    }

    // Rollover fallback: only for current week or next week. Do NOT fallback for week+2 and beyond (would show current week in all future weeks).
    if (!hasData && startDate && endDate && isRequestedWeekCurrentOrNext(startDate)) {
      const weeksBack = 1
      const prevStart = dayjs(startDate)
        .subtract(weeksBack * 7, 'day')
        .format('YYYY-MM-DD')
      const prevEnd = dayjs(endDate)
        .subtract(weeksBack * 7, 'day')
        .format('YYYY-MM-DD')

      try {
        const { data: prevData } = await extra.httpClient.get(`restaurants/${restaurantId}/eon-targets/latest`, {
          params: { startDate: prevStart, endDate: prevEnd }
        })
        const prevEntity = toTargetsEntity(prevData)
        if (prevEntity) {
          return { data: prevEntity }
        }
      } catch (e) {
        return null
      }

      return null
    }

    return entity != null ? { data: entity } : null
  }
)

export const saveEonTargets = createAsyncThunk<void, GygEonTargetsPayload, Redux>(
  'gygEonTargets/saveEonTargets',
  async (gygEonTargetsPayload, { extra }) => {
    const { restaurantId, ...payload } = gygEonTargetsPayload

    await extra.httpClient.post(`restaurants/${restaurantId}/eon-targets`, payload)
  }
)

const gygEonTargetsSlice = createSlice({
  name: 'gygEonTargets',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getEonTargetsInTwoDates.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEonTargetsInTwoDates.fulfilled, (state, action) => {
        state.loading = false
        state.success = null

        // Only store if we have a real entity (weekEnding or id); never store {} or empty wrapper
        const raw = action.payload?.data ?? action.payload
        const hasEntity =
          raw != null && typeof raw === 'object' && ((raw as any).weekEnding != null || (raw as any).id != null)
        state.targets = hasEntity ? (raw as GygEonTargets) : null
      })
      .addCase(getEonTargetsInTwoDates.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch eon goals targets'
      })
      .addCase(saveEonTargets.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(saveEonTargets.fulfilled, state => {
        state.loading = false
        state.success = 'Eon goals targets saved successfully'
        state.error = null
      })
      .addCase(saveEonTargets.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to save eon goals targets'
      })
  }
})

export default gygEonTargetsSlice.reducer
